import * as yup from 'yup';
import { NONE_OPTION_VALUE } from '../../constants';
import { OptionalModifierFieldConfig } from './types';

const REQUIRED_MESSAGE = 'This field is required';
const ISO_INSTANT_MESSAGE = 'Enter a valid ISO-8601 UTC timestamp';
const ISO_INSTANT_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/;

function isIsoInstant(value: string): boolean {
  return ISO_INSTANT_PATTERN.test(value) && !Number.isNaN(Date.parse(value));
}

/** Builds a validation schema for a single optional-modifier field. */
function buildModifierFieldSchema(
  field: OptionalModifierFieldConfig,
): yup.AnySchema {
  if (field.type === 'checkbox') {
    return yup.boolean().default(false);
  }

  let schema = yup.string();

  if (field.required) {
    schema =
      field.type === 'dropdown'
        ? schema.notOneOf([NONE_OPTION_VALUE], REQUIRED_MESSAGE)
        : schema.trim().required(REQUIRED_MESSAGE);
  }

  if (field.format === 'iso-instant') {
    schema = schema.test('iso-instant', ISO_INSTANT_MESSAGE, value => {
      if (typeof value !== 'string' || value.trim() === '') {
        return true;
      }

      return isIsoInstant(value.trim());
    });
  }

  return schema;
}

/**
 * Builds the Create Team validation schema for the given modifier field config.
 *
 * Optional-modifier rules are derived from the config, so BE-driven `required`
 * flags are enforced automatically once present.
 */
export function buildCreateTeamValidationSchema(
  modifierFieldsConfig: OptionalModifierFieldConfig[],
) {
  const modifierShape = Object.fromEntries(
    modifierFieldsConfig.map(field => [
      field.id,
      buildModifierFieldSchema(field),
    ]),
  );

  return yup.object({
    name: yup.string().trim().required(REQUIRED_MESSAGE),
    applications: yup
      .array()
      .of(yup.string().required())
      .default([])
      .min(1, REQUIRED_MESSAGE),
    parentTeamId: yup.string().required(REQUIRED_MESSAGE),
    account: yup.string().default(NONE_OPTION_VALUE),
    locationIds: yup.array().of(yup.string().required()).default([]),
    optionalModifiers: yup.object().shape(modifierShape).default({}),
  });
}
